import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useActiveProperty } from "@/context/ActivePropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { propColor } from "@/lib/propertyColors";
import { LaborComparison } from "@/components/LaborComparison";
import { PayrollStubs } from "@/components/PayrollStubs";
import { EmpDeptView } from "@/components/EmpDeptView";
import { PayrollChangeLog } from "@/components/PayrollChangeLog";
import { PendingDaysBanner } from "@/components/PendingDaysBanner";
import { RecalcBackups } from "@/components/RecalcBackups";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  CalendarClock, ChevronLeft, ChevronRight, ChevronDown, Download, Loader2, Lock, Unlock, Send, Save, CheckCircle2, RefreshCw, AlertTriangle, Clock, TriangleAlert,
} from "lucide-react";

const PTO = [
  { k: "vacation", l: "Vac" },
  { k: "sick", l: "Sick" },
  { k: "holiday", l: "Hol" },
  { k: "personal", l: "Pers" },
];

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtD = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};
const fmtDShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");
const addDaysIso = (iso, n) => {
  const d = new Date(iso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const DEPT_TONES = [
  { border: "border-indigo-500", badge: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300", row: "hover:bg-indigo-50/60 dark:hover:bg-indigo-950/20" },
  { border: "border-emerald-500", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300", row: "hover:bg-emerald-50/60 dark:hover:bg-emerald-950/20" },
  { border: "border-amber-500", badge: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300", row: "hover:bg-amber-50/60 dark:hover:bg-amber-950/20" },
  { border: "border-sky-500", badge: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300", row: "hover:bg-sky-50/60 dark:hover:bg-sky-950/20" },
  { border: "border-rose-500", badge: "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300", row: "hover:bg-rose-50/60 dark:hover:bg-rose-950/20" },
  { border: "border-violet-500", badge: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300", row: "hover:bg-violet-50/60 dark:hover:bg-violet-950/20" },
  { border: "border-teal-500", badge: "bg-teal-100 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300", row: "hover:bg-teal-50/60 dark:hover:bg-teal-950/20" },
  { border: "border-orange-500", badge: "bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300", row: "hover:bg-orange-50/60 dark:hover:bg-orange-950/20" },
];
const deptTone = (name) => {
  let h = 0;
  for (const ch of (name || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return DEPT_TONES[h % DEPT_TONES.length];
};
// min/room color: lower is faster/better
const mprColor = (v) => (!v ? "text-muted-foreground" : v <= 40 ? "text-emerald-600 dark:text-emerald-400" : v <= 55 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-500");

export default function PayrollSheet() {
  const { user } = useAuth();
  const [props, setProps] = useState([]);
  const { activePropertyId: propertyId, setActivePropertyId: setPropertyId } = useActiveProperty();
  const [ref, setRef] = useState("");
  const [data, setData] = useState(null);
  const [pto, setPto] = useState({}); // {eid: {vacation, sick, holiday, personal, comment}}
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(new Set());
  const [expandedDept, setExpandedDept] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayDetail, setDayDetail] = useState(null);
  const [dayUnsaved, setDayUnsaved] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [tab, setTab] = useState("sheet");
  const [empDetail, setEmpDetail] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);
  const [empTab, setEmpTab] = useState("all");
  const [removing, setRemoving] = useState(null);
  const canStubs = ["admin", "regional", "finance"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const openEmp = async (e) => {
    if (!data?.start) return;
    setEmpDetail({ loading: true, name: e.name, employee_id: e.employee_id });
    setEmpTab("all");
    setEmpLoading(true);
    try {
      const r = await api.get("/payroll-sheet/employee-detail", {
        params: { property_id: propertyId, start: data.start, end: data.end, employee_id: e.employee_id },
      });
      setEmpDetail(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load employee detail");
      setEmpDetail(null);
    } finally {
      setEmpLoading(false);
    }
  };

  const periodDates = useMemo(() => {
    if (!data?.start || !data?.end) return [];
    const out = [];
    let d = data.start;
    for (let i = 0; i < 32 && d <= data.end; i++) { out.push(d); d = addDaysIso(d, 1); }
    return out;
  }, [data]);

  const toggleDept = (deptName) => {
    if (expandedDept === deptName) { setExpandedDept(null); setSelectedDate(null); setDayDetail(null); return; }
    setExpandedDept(deptName); setSelectedDate(null); setDayDetail(null);
  };

  const loadDayDetail = async (deptName, date) => {
    setSelectedDate(date); setDayLoading(true); setDayDetail(null); setDayUnsaved([]);
    try {
      const r = await api.get("/payroll-sheet/day-detail", { params: { property_id: propertyId, date, department: deptName } });
      setDayDetail(r.data.rows || []);
      setDayUnsaved(r.data.unsaved_clocked || []);
    } catch {
      setDayDetail([]);
    } finally {
      setDayLoading(false);
    }
  };

  const canEdit = ["admin", "regional", "gm", "finance", "hr"].includes(user?.role);
  const empPunches = (empDetail?.punches || []).filter((p) => empTab === "all" || p.position === empTab);

  const removeSaved = async (row) => {
    const key = `${row.date}|${row.position}`;
    setRemoving(key);
    try {
      const r = await api.post("/payroll-sheet/remove-saved-entry", {
        property_id: propertyId, date: row.date, employee_id: empDetail.employee_id, position: row.position,
      });
      toast.success(`Removed ${r.data.removed_hours} hrs of ${row.position} on ${fmtDShort(row.date)}`);
      const d = await api.get("/payroll-sheet/employee-detail", {
        params: { property_id: propertyId, start: empDetail.start, end: empDetail.end, employee_id: empDetail.employee_id },
      });
      setEmpDetail(d.data);
      if (!Object.keys(d.data.saved_by_position || {}).includes(empTab) && !(d.data.punches || []).some((p) => p.position === empTab)) setEmpTab("all");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove");
    } finally {
      setRemoving(null);
    }
  };
  const canSubmit = ["admin", "regional", "gm", "finance"].includes(user?.role);
  const canReopen = ["admin", "regional", "finance"].includes(user?.role);

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data);
      if (!propertyId && r.data[0]) setPropertyId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    api.get("/payroll-sheet", { params: { property_id: propertyId, ...(ref ? { ref } : {}) } })
      .then((r) => {
        setData(r.data);
        const m = {};
        (r.data.employees || []).forEach((e) => {
          if (e.is_primary === false) return; // PTO/tips live on the primary-position row only
          m[e.employee_id] = {
            vacation: e.vacation || "", sick: e.sick || "", holiday: e.holiday || "",
            personal: e.personal || "", tips: e.tips || "", comment: e.comment || "",
          };
        });
        setPto(m);
      })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setData(null); })
      .finally(() => setLoading(false));
  }, [propertyId, ref]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!propertyId) return;
    api.get("/payroll-sheet/history", { params: { property_id: propertyId } })
      .then((r) => setSubmitted(new Set((r.data || []).map((p) => p.start))))
      .catch(() => setSubmitted(new Set()));
  }, [propertyId, data?.status]);

  // Recent pay periods for the jump-to dropdown (newest first), generated from the current period + length
  const periodOptions = useMemo(() => {
    if (!data) return [];
    const len = data.length || 14;
    const seen = new Set();
    const opts = [];
    for (let i = -1; i <= 12; i++) {
      const start = addDaysIso(data.start, -i * len);
      if (seen.has(start)) continue;
      seen.add(start);
      const end = addDaysIso(start, len - 1);
      opts.push({ start, label: `${fmtD(start)} – ${fmtD(end)}` });
    }
    return opts.sort((a, b) => (a.start < b.start ? 1 : -1));
  }, [data]);

  const locked = data?.status === "submitted";
  const setCell = (eid, field, val) =>
    setPto((m) => ({ ...m, [eid]: { ...(m[eid] || {}), [field]: val } }));

  const ptoHours = (eid) => PTO.reduce((s, p) => s + (Number(pto[eid]?.[p.k]) || 0), 0);
  const tipsOf = (eid) => Number(pto[eid]?.tips) || 0;
  const grossOf = (e) => {
    const tips = e.is_primary === false ? 0 : tipsOf(e.employee_id);
    if (e.pay_type === "salaried" && e.annual_salary > 0) return e.gross_pay + tips;
    const base = e.regular_hours * e.rate + e.ot_hours * e.rate * 1.5;
    // PTO + tips are per-employee — only applied on the primary-position row
    return base + (e.is_primary === false ? 0 : ptoHours(e.employee_id) * e.rate) + tips;
  };

  const savePto = async () => {
    setSaving(true);
    try {
      await api.post("/payroll-sheet/pto", { property_id: propertyId, start: data.start, end: data.end, entries: pto });
      toast.success("PTO & comments saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submit = async () => {
    if (!window.confirm("Submit this pay period? It will be locked for the accountant. You can reopen it later if needed.")) return;
    setBusy(true);
    try {
      await api.post("/payroll-sheet/pto", { property_id: propertyId, start: data.start, end: data.end, entries: pto });
      await api.post("/payroll-sheet/submit", { property_id: propertyId, start: data.start });
      toast.success("Pay period submitted & locked");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await api.post("/payroll-sheet/reopen", { property_id: propertyId, start: data.start });
      toast.success("Pay period reopened");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const exportXlsx = async () => {
    setBusy(true);
    try {
      const res = await api.get("/payroll-sheet/export", { params: { property_id: propertyId, ref: data.start }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const name = (props.find((p) => p.id === propertyId)?.name || "Payroll").replace(/\s+/g, "_");
      a.href = url; a.download = `${name}_Payroll_${data.start}_to_${data.end}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      let detail = e.response?.data?.detail;
      if (e.response?.data instanceof Blob) { try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* noop */ } }
      toast.error(formatApiErrorDetail(detail));
    } finally { setBusy(false); }
  };

  const t = data?.totals || {};

  const syncFromTimeClock = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payroll-sheet/sync-timeclock", { property_id: propertyId, start: data.start, end: data.end });
      if (r.data.created_days > 0) {
        toast.success(`Saved ${r.data.created_days} day(s) from the Time Clock`);
        load();
      } else {
        toast.info("Nothing to save — every day with punches already has a saved Daily Payroll Entry");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const recalcFromTimeClock = async () => {
    if (!window.confirm("Recalculate this pay period from the Time Clock?\n\nThis REPLACES saved hours for anyone who clocked in with their Time Clock hours for each position — use it to fix stale/wrong hours after correcting a punch. Rooms already entered and PTO are always kept. A backup of every changed day is saved (Admins can undo it on the Change Log tab). Flagged (too-long) punches are excluded.")) return;
    setBusy(true);
    try {
      const r = await api.post("/payroll-sheet/sync-timeclock", { property_id: propertyId, start: data.start, end: data.end, overwrite: true });
      if (r.data.created_days > 0) {
        toast.success(`Recalculated ${r.data.created_days} day(s) from the Time Clock`);
        load();
      } else {
        toast.info("Nothing changed — saved hours already match the Time Clock");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-sheet-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight flex items-center gap-2">
            <CalendarClock className="h-7 w-7 text-primary" /> Payroll Sheet
          </h1>
          <p className="text-sm text-muted-foreground">Clocked hours, rooms cleaned & PTO for each pay period — export or submit to your accountant.</p>
        </div>
        <div>
          <Label className="label-caps text-[0.6rem] text-muted-foreground">Property</Label>
          <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setRef(""); }}>
            <SelectTrigger className="w-56" data-testid="ps-property-select"><SelectValue placeholder="Property" /></SelectTrigger>
            <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}><span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: propColor(p) }} />{p.name}</span></SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {canStubs && (
        <div className="flex gap-1 border-b border-border" data-testid="payroll-tabs">
          <button onClick={() => setTab("sheet")} data-testid="tab-payroll-sheet"
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === "sheet" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Payroll Sheet</button>
          <button onClick={() => setTab("stubs")} data-testid="tab-payroll-stubs"
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === "stubs" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Payroll Stubs</button>
          {isAdmin && (
            <button onClick={() => setTab("changes")} data-testid="tab-payroll-changes"
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === "changes" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>Change Log</button>
          )}
        </div>
      )}

      {tab === "stubs" && canStubs && <PayrollStubs propertyId={propertyId} />}
      {tab === "changes" && isAdmin && (
        <div className="space-y-6">
          <RecalcBackups propertyId={propertyId} onChanged={load} />
          <PayrollChangeLog propertyId={propertyId} start={data?.start} end={data?.end} propertyName={props.find((p) => p.id === propertyId)?.name} />
        </div>
      )}

      {tab === "sheet" && (!data ? (
        <Card className="flex flex-col items-center justify-center rounded-none border py-16 text-muted-foreground">
          {loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <><CalendarClock className="h-8 w-8" /><p className="mt-3 text-sm">Select a property to begin.</p></>}
        </Card>
      ) : (
        <>
          {/* Period nav + actions */}
          <div className="sticky top-0 z-20 -mx-4 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-background/95 px-4 py-2.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => setRef(data.prev_ref)} data-testid="ps-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <div className="min-w-56 text-center">
                <div className="font-bold" data-testid="ps-period-label">{fmtD(data.start)} – {fmtD(data.end)}</div>
                <div className="text-xs capitalize text-muted-foreground">{data.frequency} pay period</div>
              </div>
              <Button variant="outline" size="icon" onClick={() => setRef(data.next_ref)} data-testid="ps-next"><ChevronRight className="h-4 w-4" /></Button>
              <Select value={data.start} onValueChange={(v) => setRef(v)}>
                <SelectTrigger className="w-52" data-testid="ps-period-select"><SelectValue placeholder="Jump to period…" /></SelectTrigger>
                <SelectContent>
                  {periodOptions.map((o) => (
                    <SelectItem key={o.start} value={o.start} data-testid={`ps-period-opt-${o.start}`}>
                      <span className="inline-flex items-center gap-2">
                        {o.label}
                        {submitted.has(o.start) && <Lock className="h-3 w-3 text-emerald-500" />}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {locked ? (
                <span className="ml-2 flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-600 dark:text-emerald-400" data-testid="ps-status">
                  <Lock className="h-3 w-3" /> Submitted {data.submitted_by ? `by ${data.submitted_by}` : ""}
                </span>
              ) : (
                <span className="ml-2 rounded-full bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-600 dark:text-amber-400" data-testid="ps-status">Draft</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {!locked && canEdit && (
                <Button variant="outline" onClick={savePto} disabled={saving} data-testid="ps-save-btn">
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save
                </Button>
              )}
              <Button variant="outline" onClick={exportXlsx} disabled={busy} data-testid="ps-export-btn"><Download className="mr-2 h-4 w-4" /> Export Excel</Button>
              {!locked && canEdit && (
                <Button variant="outline" onClick={syncFromTimeClock} disabled={busy} data-testid="ps-sync-timeclock-btn"><CalendarClock className="mr-2 h-4 w-4" /> Save from Time Clock</Button>
              )}
              {!locked && canEdit && (
                <Button variant="outline" onClick={recalcFromTimeClock} disabled={busy} data-testid="ps-recalc-timeclock-btn"><RefreshCw className="mr-2 h-4 w-4" /> Recalculate from Time Clock</Button>
              )}
              {!locked && canSubmit && (
                <Button onClick={submit} disabled={busy} data-testid="ps-submit-btn"><Send className="mr-2 h-4 w-4" /> Submit</Button>
              )}
              {locked && canReopen && (
                <Button variant="outline" onClick={reopen} disabled={busy} data-testid="ps-reopen-btn"><Unlock className="mr-2 h-4 w-4" /> Reopen</Button>
              )}
            </div>
          </div>

          <PendingDaysBanner days={data.pending_days} propertyId={propertyId} locked={data.status === "submitted"} canApprove={canEdit} onApproved={load} />

          {/* Labor exceptions — saved payroll hours vs approved time clock */}
          {(data.labor_exceptions || []).length > 0 && (
            <div className="rounded-none border border-amber-400/60 bg-amber-50 p-4 dark:bg-amber-950/20" data-testid="ps-labor-exceptions">
              <div className="mb-2 flex items-center gap-2 text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-sm font-bold">
                  {data.labor_exceptions.length} labor exception{data.labor_exceptions.length > 1 ? "s" : ""} — saved hours don't match the Time Clock
                </span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                These employees' saved payroll hours for this period differ from their approved clocked hours. An <span className="font-semibold text-foreground">over</span> usually means hours were double-counted across positions; an <span className="font-semibold text-foreground">under</span> means a saved day has fewer hours than clocked. Days not yet approved and today are not counted here. Open the affected day on the <span className="font-medium text-foreground">Daily Report</span> and use <span className="font-medium text-foreground">Auto-balance</span>, or click <span className="font-medium text-foreground">Recalculate from Time Clock</span> above.
              </p>
              <div className="space-y-1">
                {data.labor_exceptions.map((x) => (
                  <div key={x.employee_id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-amber-400/40 bg-white px-3 py-1.5 text-sm dark:bg-transparent" data-testid={`ps-exception-${x.employee_id}`}>
                    <span className="font-medium">{x.name}</span>
                    <span className="flex items-center gap-3 font-mono text-xs">
                      <span>saved <b className="tabular-nums">{x.saved_hours.toFixed(2)}</b></span>
                      <span className="text-muted-foreground">clocked <b className="tabular-nums">{x.clocked_hours.toFixed(2)}</b></span>
                      <span className={`rounded-sm px-1.5 py-0.5 font-bold ${x.kind === "over" ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-400"}`}>
                        {x.diff > 0 ? "+" : ""}{x.diff.toFixed(2)} {x.kind}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["Reg Hrs", t.regular], ["OT Hrs", t.ot], ["PTO Hrs", t.pto],
              ["Rooms Cleaned", t.rooms_cleaned], ["Min / Room", t.minutes_per_room], ["Gross Pay", money(t.gross)],
            ].map(([l, v]) => (
              <Card key={l} className="rounded-none border p-3" data-testid={`ps-total-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="label-caps text-[0.55rem] text-muted-foreground">{l}</div>
                <div className="text-lg font-black tabular-nums">{v}</div>
              </Card>
            ))}
          </div>

          {/* Employee table */}
          <div className="overflow-x-auto rounded-none border">
            <table className="w-full min-w-[1100px] text-sm" data-testid="ps-table">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="p-2">Employee</th>
                  <th className="p-2">Dept</th>
                  <th className="p-2 text-right">Rate</th>
                  <th className="p-2 text-right">Reg</th>
                  <th className="p-2 text-right">OT</th>
                  <th className="p-2 text-right">Tips ($)</th>
                  {PTO.map((p) => <th key={p.k} className="p-2 text-right">{p.l}</th>)}
                  <th className="p-2 text-right">PTO Avail</th>
                  <th className="p-2 text-right">Sick Avail</th>
                  <th className="p-2 text-right">Stay</th>
                  <th className="p-2 text-right">Chk</th>
                  <th className="p-2 text-right">Rooms</th>
                  <th className="p-2 text-right">Min/Rm</th>
                  <th className="p-2 text-right">Gross</th>
                  <th className="p-2">Comment</th>
                </tr>
              </thead>
              <tbody>
                {data.employees.length === 0 ? (
                  <tr><td colSpan={18} className="p-8 text-center text-muted-foreground">No employees for this property.</td></tr>
                ) : data.employees.map((e) => {
                  const rk = e.row_key || e.employee_id;
                  const primary = e.is_primary !== false;
                  return (
                  <tr key={rk} className="border-b hover:bg-muted/30" data-testid={`ps-row-${rk}`}>
                    <td className="p-2 font-medium">
                      <button type="button" onClick={() => openEmp(e)} className="text-left font-medium text-primary underline-offset-2 hover:underline" data-testid={`ps-emp-${rk}`}>
                        {e.name}
                      </button>
                      {e.pay_type === "salaried" && <span className="ml-1 text-[0.6rem] text-muted-foreground">(sal)</span>}
                      {!primary && <span className="ml-2 rounded-sm bg-muted px-1.5 py-0.5 text-[0.55rem] font-semibold uppercase tracking-wide text-muted-foreground">2nd</span>}
                      {primary && e.term_date && <span className="ml-2 rounded-sm bg-red-500/15 px-1.5 py-0.5 text-[0.55rem] font-semibold uppercase tracking-wide text-red-700 dark:text-red-400" title="Final paycheck — removed from the sheet from next period" data-testid={`ps-terminated-${rk}`}>Terminated {e.term_date}</span>}
                    </td>
                    <td className="p-2 text-muted-foreground">{e.department}</td>
                    <td className="p-2 text-right tabular-nums">{e.pay_type === "salaried" ? "—" : money(e.rate)}</td>
                    <td className="p-2 text-right tabular-nums">{e.regular_hours}</td>
                    <td className="p-2 text-right tabular-nums">{e.ot_hours > 0 ? <span className="font-semibold text-amber-600 dark:text-amber-400">{e.ot_hours}</span> : "0"}</td>
                    <td className="p-1 text-right">
                      {primary ? (
                        <Input
                          value={pto[e.employee_id]?.tips ?? ""}
                          onChange={(ev) => setCell(e.employee_id, "tips", ev.target.value.replace(/[^0-9.]/g, ""))}
                          disabled={locked || !canEdit}
                          inputMode="decimal" placeholder="0"
                          className="h-8 w-16 px-1 text-right tabular-nums"
                          data-testid={`ps-tips-${e.employee_id}`}
                        />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    {PTO.map((p) => (
                      <td key={p.k} className="p-1 text-right">
                        {primary ? (
                          <Input
                            value={pto[e.employee_id]?.[p.k] ?? ""}
                            onChange={(ev) => setCell(e.employee_id, p.k, ev.target.value.replace(/[^0-9.]/g, ""))}
                            disabled={locked || !canEdit}
                            inputMode="decimal" placeholder="0"
                            className="h-8 w-14 px-1 text-right tabular-nums"
                            data-testid={`ps-${p.k}-${e.employee_id}`}
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    ))}
                    <td className="p-2 text-right tabular-nums" data-testid={`ps-avail-${rk}`}>
                      {primary && e.pto_accrual_enabled
                        ? <span className="font-medium text-emerald-600 dark:text-emerald-400">{Number(e.pto_balance || 0).toFixed(1)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2 text-right tabular-nums" data-testid={`ps-sick-avail-${rk}`}>
                      {primary && e.sick_accrual_enabled
                        ? <span className="font-medium text-sky-600 dark:text-sky-400">{Number(e.sick_balance || 0).toFixed(1)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2 text-right tabular-nums">{e.stayover}</td>
                    <td className="p-2 text-right tabular-nums">{e.checkout}</td>
                    <td className="p-2 text-right font-semibold tabular-nums">{e.rooms_cleaned}</td>
                    <td className="p-2 text-right tabular-nums text-muted-foreground">{e.minutes_per_room || "—"}</td>
                    <td className="p-2 text-right font-bold tabular-nums" data-testid={`ps-gross-${rk}`}>{money(grossOf(e))}</td>
                    <td className="p-1">
                      {primary ? (
                        <Input
                          value={pto[e.employee_id]?.comment ?? ""}
                          onChange={(ev) => setCell(e.employee_id, "comment", ev.target.value)}
                          disabled={locked || !canEdit}
                          placeholder="Note…"
                          className="h-8 min-w-[140px] text-xs"
                          data-testid={`ps-comment-${e.employee_id}`}
                        />
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* By department */}
          {data.by_department?.length > 0 && (
            <div>
              <h2 className="mb-2 text-base font-bold">All Department Hours <span className="text-xs font-normal text-muted-foreground">(click a department to see detail)</span></h2>
              <div className="overflow-x-auto rounded-lg border shadow-sm">
                <table className="w-full min-w-[720px] text-sm" data-testid="ps-dept-table">
                  <thead>
                    <tr className="border-b bg-primary/10 text-left text-xs font-semibold uppercase tracking-wide text-primary">
                      <th className="p-2.5">Department</th>
                      <th className="p-2.5 text-right">Reg Hrs</th>
                      <th className="p-2.5 text-right">OT Hrs</th>
                      <th className="p-2.5 text-right">PTO Hrs</th>
                      <th className="p-2.5 text-right">Total Hrs</th>
                      <th className="p-2.5 text-right">Stay</th>
                      <th className="p-2.5 text-right">Chk</th>
                      <th className="p-2.5 text-right">Rooms</th>
                      <th className="p-2.5 text-right">Min/Rm</th>
                      <th className="p-2.5 text-right">Gross</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_department.map((d) => {
                      const open = expandedDept === d.department;
                      const deptEmps = data.employees.filter((e) => (e.department || "(none)") === d.department);
                      const tone = deptTone(d.department);
                      return (
                        <Fragment key={d.department}>
                          <tr className={`cursor-pointer border-b transition-colors ${open ? "bg-primary/5" : tone.row}`} data-testid={`ps-dept-${d.department}`} onClick={() => toggleDept(d.department)}>
                            <td className={`border-l-4 p-2.5 font-medium ${tone.border}`}>
                              <span className="inline-flex items-center gap-1.5">
                                {open ? <ChevronDown className="h-4 w-4 text-primary" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${tone.badge}`}>{d.department}</span>
                              </span>
                            </td>
                            <td className="p-2.5 text-right tabular-nums">{d.regular}</td>
                            <td className="p-2.5 text-right tabular-nums">{d.ot > 0 ? <span className="rounded bg-amber-100 px-1.5 py-0.5 font-bold text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">{d.ot}</span> : <span className="text-muted-foreground">0</span>}</td>
                            <td className="p-2.5 text-right tabular-nums text-muted-foreground">{d.pto}</td>
                            <td className="p-2.5 text-right font-bold tabular-nums text-primary">{d.total_hours}</td>
                            <td className="p-2.5 text-right tabular-nums">{d.stayover || 0}</td>
                            <td className="p-2.5 text-right tabular-nums">{d.checkout || 0}</td>
                            <td className="p-2.5 text-right font-semibold tabular-nums">{d.rooms}</td>
                            <td className={`p-2.5 text-right font-semibold tabular-nums ${mprColor(d.minutes_per_room)}`}>{d.minutes_per_room ? d.minutes_per_room : "—"}</td>
                            <td className="p-2.5 text-right font-bold tabular-nums text-emerald-700 dark:text-emerald-400">{money(d.gross)}</td>
                          </tr>
                          {open && (
                            <tr className="border-b bg-muted/20" data-testid={`ps-dept-detail-${d.department}`}>
                              <td colSpan={10} className="p-3">
                                {(() => {
                                  const series = data.dept_daily?.[d.department] || [];
                                  if (series.length === 0) return null;
                                  const vals = series.map((s) => s.minutes_per_room);
                                  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
                                  const max = Math.max(...vals, 1);
                                  const color = (v) => (v > avg * 1.15 ? "bg-red-500" : v > avg ? "bg-amber-500" : "bg-emerald-500");
                                  return (
                                    <div className="mb-4" data-testid={`ps-trend-${d.department}`}>
                                      <div className="mb-1 flex items-center justify-between">
                                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Min/Room per day <span className="normal-case text-muted-foreground/70">(taller/red = slower)</span></div>
                                        <div className="text-xs text-muted-foreground">avg {avg.toFixed(1)} min</div>
                                      </div>
                                      <div className="flex items-end gap-1.5 overflow-x-auto rounded border bg-background p-3" style={{ minHeight: 90 }}>
                                        {series.map((s) => (
                                          <button key={s.date} onClick={() => loadDayDetail(d.department, s.date)} title={`${s.date}: ${s.minutes_per_room} min/room · ${s.rooms} rooms`}
                                            className="flex w-12 shrink-0 flex-col items-center gap-1" data-testid={`ps-trendbar-${d.department}-${s.date}`}>
                                            <span className="text-[0.6rem] font-mono font-semibold tabular-nums">{s.minutes_per_room}</span>
                                            <div className={`w-full rounded-t ${color(s.minutes_per_room)} ${selectedDate === s.date ? "ring-2 ring-primary ring-offset-1" : ""}`} style={{ height: Math.max(6, (s.minutes_per_room / max) * 48) }} />
                                            <span className="text-[0.55rem] text-muted-foreground">{new Date(s.date + "T00:00:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                })()}
                                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{d.department} — employees this period</div>
                                <div className="overflow-x-auto rounded border bg-background">
                                  <table className="w-full min-w-[640px] text-sm">
                                    <thead>
                                      <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                                        <th className="p-2">Employee</th>
                                        <th className="p-2 text-right">Reg</th>
                                        <th className="p-2 text-right">OT</th>
                                        <th className="p-2 text-right">Total Hrs</th>
                                        <th className="p-2 text-right">Stay</th>
                                        <th className="p-2 text-right">Chk</th>
                                        <th className="p-2 text-right">Rooms</th>
                                        <th className="p-2 text-right">Min/Rm</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {deptEmps.map((e) => (
                                        <tr key={e.row_key || e.employee_id} className="border-b last:border-0">
                                          <td className="p-2 font-medium">{e.name}</td>
                                          <td className="p-2 text-right tabular-nums">{e.regular_hours}</td>
                                          <td className="p-2 text-right tabular-nums">{e.ot_hours}</td>
                                          <td className="p-2 text-right font-semibold tabular-nums">{(e.worked_hours + e.pto_hours).toFixed(2)}</td>
                                          <td className="p-2 text-right tabular-nums">{e.stayover}</td>
                                          <td className="p-2 text-right tabular-nums">{e.checkout}</td>
                                          <td className="p-2 text-right tabular-nums">{e.rooms_cleaned}</td>
                                          <td className="p-2 text-right tabular-nums">{e.minutes_per_room || "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>

                                <div className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pick a date for daily detail</div>
                                <div className="flex flex-wrap gap-1.5">
                                  {periodDates.map((dt) => (
                                    <button key={dt} onClick={() => loadDayDetail(d.department, dt)} data-testid={`ps-daybtn-${d.department}-${dt}`}
                                      className={`rounded-md border px-2 py-1 text-xs transition-colors ${selectedDate === dt ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-muted"}`}>
                                      {new Date(dt + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })}
                                    </button>
                                  ))}
                                </div>

                                {selectedDate && (
                                  <div className="mt-3" data-testid="ps-day-detail">
                                    <div className="mb-1 text-sm font-semibold">{d.department} — {fmtD(selectedDate)}</div>
                                    {dayLoading ? (
                                      <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
                                    ) : (dayDetail && dayDetail.length > 0) ? (
                                      <div className="overflow-x-auto rounded border bg-background">
                                        <table className="w-full min-w-[560px] text-sm">
                                          <thead>
                                            <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                                              <th className="p-2">Employee</th>
                                              <th className="p-2 text-right">Hours</th>
                                              <th className="p-2 text-right">Stayover</th>
                                              <th className="p-2 text-right">Checkout</th>
                                              <th className="p-2 text-right">Rooms</th>
                                              <th className="p-2 text-right">Min/Room</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {dayDetail.map((r) => (
                                              <tr key={r.employee_id} className="border-b last:border-0">
                                                <td className="p-2 font-medium">{r.name}</td>
                                                <td className="p-2 text-right tabular-nums">{r.hours}</td>
                                                <td className="p-2 text-right tabular-nums">{r.stayover}</td>
                                                <td className="p-2 text-right tabular-nums">{r.checkout}</td>
                                                <td className="p-2 text-right font-semibold tabular-nums">{r.rooms}</td>
                                                <td className="p-2 text-right tabular-nums">{r.minutes_per_room || "—"}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="p-2">
                                        {dayUnsaved && dayUnsaved.length > 0 ? (
                                          <div className="rounded border border-amber-500/50 bg-amber-500/10 p-3 text-sm" data-testid="ps-unsaved-hint">
                                            <p className="font-semibold text-amber-700 dark:text-amber-400">Time Clock has punches for this day that aren't saved to Daily Payroll Entry yet.</p>
                                            <p className="mt-1 text-xs text-muted-foreground">The Payroll Sheet totals come from Daily Payroll Entry. Open <b>Daily Report Entry → Payroll tab</b> for {fmtD(selectedDate)} — hours pre-fill from the Time Clock — then click <b>Save</b> to include them here.</p>
                                            <ul className="mt-2 space-y-0.5 text-xs">
                                              {dayUnsaved.map((u) => (
                                                <li key={u.employee_id} className="flex justify-between"><span>{u.name}</span><span className="tabular-nums font-medium">{u.hours} hrs</span></li>
                                              ))}
                                            </ul>
                                          </div>
                                        ) : (
                                          <p className="text-sm text-muted-foreground">No entries for this department on {fmtD(selectedDate)}.</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <LaborComparison propertyId={propertyId} lockedRef={data.start} />

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" /> Hours pull from the Time Clock; rooms pull from kiosk Stayover/Checkout cleanings. Overtime is anything over 40 hrs/week. Set the pay frequency & start date on the <span className="font-semibold">Property</span> page.
          </p>
        </>
      ))}

      {/* Employee drill-down: all period hours + every time-clock punch */}
      <Dialog open={!!empDetail} onOpenChange={(o) => !o && setEmpDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="ps-emp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{empDetail?.name}</DialogTitle>
            <DialogDescription>
              {empDetail?.start ? `${fmtD(empDetail.start)} – ${fmtD(empDetail.end)}` : "Pay period"} · all hours & time-clock punches
            </DialogDescription>
          </DialogHeader>

          {empLoading || empDetail?.loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : empDetail ? (
            <div className="space-y-5">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-border p-3" data-testid="ps-emp-saved-total">
                  <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">Saved Payroll Hrs</div>
                  <div className="mt-0.5 text-xl font-bold tabular-nums">{Number(empDetail.saved_total || 0).toFixed(2)}</div>
                </div>
                <div className="rounded-md border border-border p-3" data-testid="ps-emp-clocked-total">
                  <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">Approved Clocked Hrs</div>
                  <div className="mt-0.5 text-xl font-bold tabular-nums text-muted-foreground">{Number(empDetail.clocked_total || 0).toFixed(2)}</div>
                </div>
                {Math.abs(Number(empDetail.saved_total || 0) - Number(empDetail.clocked_total || 0)) > 0.1 && (
                  <div className="col-span-2 flex items-center gap-2 rounded-md border border-amber-400/60 bg-amber-50 p-3 text-xs text-amber-700 dark:bg-amber-950/20 dark:text-amber-400 sm:col-span-1" data-testid="ps-emp-mismatch">
                    <TriangleAlert className="h-4 w-4 shrink-0" />
                    <span>Saved hours don't match the clock for this period.</span>
                  </div>
                )}
              </div>

              <EmpDeptView
                detail={empDetail}
                tab={empTab}
                setTab={setEmpTab}
                canRemove={canEdit && empDetail.status !== "submitted"}
                removing={removing}
                onRemove={removeSaved}
              />

              {/* Time clock punches */}
              <div>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-bold"><Clock className="h-4 w-4" /> Time Clock Punches{empTab !== "all" ? ` — ${empTab}` : ""} ({empPunches.length})</h3>
                {empPunches.length === 0 ? (
                  <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground" data-testid="ps-emp-no-punches">
                    {empTab !== "all"
                      ? <span className="font-medium text-red-600 dark:text-red-400">No clock-in under {empTab} this period — hours saved here are not backed by the Time Clock (likely a duplicate).</span>
                      : "No time-clock punches recorded for this period."}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Position</th>
                          <th className="px-3 py-2">Clock In</th>
                          <th className="px-3 py-2">Clock Out</th>
                          <th className="px-3 py-2 text-right">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empPunches.map((p, i) => (
                          <tr key={i} className="border-t border-border" data-testid={`ps-emp-punch-${i}`}>
                            <td className="px-3 py-2 whitespace-nowrap">{fmtDShort(p.date)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{p.position || "—"}</td>
                            <td className="px-3 py-2 tabular-nums">{fmtT(p.clock_in)}</td>
                            <td className="px-3 py-2 tabular-nums">{p.open ? <span className="text-amber-600 dark:text-amber-400">still in</span> : fmtT(p.clock_out)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {p.flagged ? (
                                <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title="Punch flagged as too long — excluded from payroll until corrected">
                                  <TriangleAlert className="h-3 w-3" /> flagged
                                </span>
                              ) : p.open ? "—" : p.hours.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-border bg-muted/30 font-bold">
                          <td className="px-3 py-2" colSpan={4}>Approved clocked total{empTab !== "all" ? ` (${empTab})` : ""}</td>
                          <td className="px-3 py-2 text-right tabular-nums" data-testid="ps-emp-punch-total">{(empTab === "all" ? Number(empDetail.clocked_total || 0) : empPunches.reduce((a, p) => a + (p.flagged || p.open ? 0 : p.hours || 0), 0)).toFixed(2)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
                <p className="mt-1.5 text-[0.65rem] text-muted-foreground">Flagged punches (missing clock-out or over the max shift length) are excluded from the approved total until a manager corrects them.</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
